import IDate from '@dvrd/idate';
import ProductCostsV2, {ProductCostsV2Fields} from '../models/productCostsV2Model';
import {ISO_DATE_FORMAT} from './constants';
import {ProductCostsSection, ProductCostsValidation} from './types';

const AMOUNT_FIELDS: Array<Partial<keyof ProductCostsV2Fields>> = [
    'cost_price',
    'pick_costs',
    'send_costs',
    'misc_return_costs',
    'misc_costs'
];

// Where each amount input lives now that the form is split up: a rejected value
// in a collapsed section would otherwise fail silently. `cost_price` sits above
// the sections and needs no reveal.
const AMOUNT_SECTIONS: Partial<Record<keyof ProductCostsV2Fields, ProductCostsSection | null>> = {
    cost_price: null,
    pick_costs: 'fulfilment',
    send_costs: 'fulfilment',
    misc_return_costs: 'returns',
    misc_costs: 'misc'
};

export function sectionsWithErrors(validation: ProductCostsValidation): Array<ProductCostsSection> {
    const sections = new Set<ProductCostsSection>();
    for (const key of AMOUNT_FIELDS) {
        const section = AMOUNT_SECTIONS[key];
        if (section && validation[key]) sections.add(section);
    }
    return Array.from(sections);
}

function toIsoDate(date: IDate): string {
    return date.format(ISO_DATE_FORMAT);
}

function recordDate(costs: ProductCostsV2): string {
    return toIsoDate(costs.startDateAsDate);
}

function byDateDesc(a: ProductCostsV2, b: ProductCostsV2): number {
    return recordDate(b).localeCompare(recordDate(a));
}

function byDateDescThenQuantity(a: ProductCostsV2, b: ProductCostsV2): number {
    const byDate = byDateDesc(a, b);
    return byDate !== 0 ? byDate : (a.minQuantity ?? 1) - (b.minQuantity ?? 1);
}

/**
 * The base tier is what the product list shows as "the" cost price, so a
 * quantity tier must never win just by being newer. Higher tiers are a fallback
 * for products that only have tiered costs.
 */
function pickBaseTierFirst(records: Array<ProductCostsV2>): ProductCostsV2 | null {
    if (!records.length) return null;
    const baseTier = records.filter(record => (record.minQuantity ?? 1) === 1);
    return [...(baseTier.length ? baseTier : records)].sort(byDateDescThenQuantity)[0];
}

export function findCurrentCosts(records: Array<ProductCostsV2>, today: IDate): ProductCostsV2 | null {
    const todayIso = toIsoDate(today);
    const applicable = records.filter(record => recordDate(record) <= todayIso);
    return pickBaseTierFirst(applicable) ?? pickBaseTierFirst(records);
}

function isForever(costs: ProductCostsV2): boolean {
    return costs.getFlex<boolean>('forever_selected') === true;
}

export function createProductCostsForm(
    record: ProductCostsV2 | null,
    ids: {accountId: string; ean: string} | null
): ProductCostsV2 {
    if (record) return record.clone();
    return new ProductCostsV2({
        start_date: '2020-01-01',
        account_id: ids?.accountId,
        ean: ids?.ean,
        flex: {forever_selected: true}
    });
}

export function buildPeriodEndDates(records: Array<ProductCostsV2>): Map<string, string | null> {
    const ends = new Map<string, string | null>();
    const tiers = new Map<number, Array<ProductCostsV2>>();
    for (const record of records) {
        const tier = record.minQuantity ?? 1;
        tiers.set(tier, [...(tiers.get(tier) ?? []), record]);
    }
    for (const tierRecords of tiers.values()) {
        const sorted = [...tierRecords].sort((a, b) => recordDate(a).localeCompare(recordDate(b)));
        for (let index = 0; index < sorted.length; index++) {
            const next = sorted[index + 1];
            ends.set(sorted[index].id, next ? toIsoDate(next.startDateAsDate.subtract(1, 'day')) : null);
        }
    }
    return ends;
}

export function findDuplicatePeriod(
    records: Array<ProductCostsV2>,
    productCosts: ProductCostsV2
): ProductCostsV2 | null {
    return (
        records.find((record: ProductCostsV2) => {
            if (record.id === productCosts.id) return false;
            if ((record.minQuantity ?? 1) !== (productCosts.minQuantity ?? 1)) return false;
            if (isForever(productCosts) || isForever(record)) return true;
            return recordDate(record) === recordDate(productCosts);
        }) ?? null
    );
}

export function validateProductCostsForm(form: ProductCostsV2): ProductCostsValidation {
    const errors: ProductCostsValidation = {};
    if (form.costPrice === null || form.costPrice === 0) errors.cost_price = 'invalid';
    else if (form.costPrice < 0) errors.cost_price = 'negative';

    if (form.pickCosts !== null && form.pickCosts < 0) errors.pick_costs = 'negative';
    if (form.sendCosts !== null && form.sendCosts < 0) errors.send_costs = 'negative';
    if (form.miscReturnCosts !== null && form.miscReturnCosts < 0) errors.misc_return_costs = 'negative';
    if (form.miscCosts !== null && form.miscCosts < 0) errors.misc_costs = 'negative';
    return errors;
}
